"use client";

import Link from "next/link";
import { useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { ArrowUp, Layers, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { LaunchContextChip } from "@/components/project-home/LaunchContextChip";
import { ProjectHomeLaunchContextPicker } from "@/components/project-home/ProjectHomeLaunchContextPicker";
import { ProjectAnalyticsPulse } from "@/components/project-analytics-pulse";
import { useAuth } from "@/contexts/auth-context";
import { useAutoResizeTextarea } from "@/hooks/useAutoResizeTextarea";
import { useIsMdUp } from "@/hooks/useIsMdUp";
import { completeLaunchAfterNavigate } from "@/lib/launch/completeLaunchAfterNavigate";
import { defaultLaunchMessageWhenEmpty } from "@/lib/launch/composeLaunchUserMessage";
import { rectFromElement, resolveLaunchHandoffMode } from "@/lib/launch/launchHandoff";
import type { MentionReference } from "@/lib/mentions/types";
import { CHAT_ACCEPT_EXTENSIONS, isAllowedChatFile } from "@/constants/allowedFileTypes";
import { buildProjectHomeWaitingDeepLink } from "@/lib/entityDeepLinks";
import { useConversationStore } from "@/stores/useConversationStore";
import { useCurrentUserSharesStore } from "@/stores/useCurrentUserSharesStore";
import { useLaunchHandoffStore } from "@/stores/useLaunchHandoffStore";
import { usePendingConversationPromptStore } from "@/stores/usePendingConversationPromptStore";
import { getErrorMessage } from "@/types/problem-detail";
import { cn } from "@/lib/utils";
import { PROJECT_HUB_COLUMN_CLASS } from "@/lib/projectHubLayout";
const LAUNCH_COMPOSER_ACTION_CLASS = cn("inline-flex size-8 shrink-0 items-center justify-center rounded-full", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50", "disabled:pointer-events-none disabled:opacity-40");

/** @deprecated Use PROJECT_HUB_COLUMN_CLASS from @/lib/projectHubLayout */
export const PROJECT_HOME_COLUMN_CLASS = PROJECT_HUB_COLUMN_CLASS;
const MAX_TITLE_LENGTH = 80;
export const PROJECT_HOME_INTENT_FIELD_ID = "project-home-intent-field";
export interface ProjectHomeIntentFieldProps {
  projectId: string;
  className?: string;
  /** Project liveness line from {@link useProjectHomeLiveness}. */
  livenessLine?: string | null;
  /** Open decision count; when positive, liveness line links to #waiting. */
  waitingCount?: number;
}

/**
 * Launch control on the project home: start a conversation, optionally attach
 * documents, then continue into the workbench composer. Creates a conversation
 * and seeds the first message via {@link usePendingConversationPromptStore}.
 * No assistant-ui runtime.
 */
export function ProjectHomeIntentField({
  projectId,
  className,
  livenessLine = null,
  waitingCount = 0
}: ProjectHomeIntentFieldProps) {
  const router = useRouter();
  const {
    user
  } = useAuth();
  const createConversation = useConversationStore(s => s.createConversation);
  const setPrompt = usePendingConversationPromptStore(s_0 => s_0.setPrompt);
  const beginPreparing = useLaunchHandoffStore(s_1 => s_1.beginPreparing);
  const beginNavigating = useLaunchHandoffStore(s_2 => s_2.beginNavigating);
  const abortHandoff = useLaunchHandoffStore(s_3 => s_3.abort);
  const sharesInitialized = useCurrentUserSharesStore(s_4 => s_4.isInitialized);
  const roleForEntity = useCurrentUserSharesStore(s_5 => s_5.roleForEntity);
  const fieldRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const prefetchedWorkbenchRef = useRef(false);
  const [text, setText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [contextMentions, setContextMentions] = useState<MentionReference[]>([]);
  const [contextPanelOpen, setContextPanelOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const isMdUp = useIsMdUp();
  const {
    ref: textareaRef,
    onPaste: handleTextareaPaste
  } = useAutoResizeTextarea(text);
  const isViewer = sharesInitialized && roleForEntity("project", projectId) === "viewer";
  const busy = creating;
  const handoffPhase = useLaunchHandoffStore(s_6 => s_6.phase);
  const hideFieldDuringHandoff = handoffPhase === "preparing" || handoffPhase === "navigating";
  const firstName = user?.firstName?.trim();
  const greeting = firstName ? `Hi ${firstName}, what should we figure out?` : "What should we figure out?";
  const prefetchWorkbenchShell = () => {
    if (prefetchedWorkbenchRef.current) return;
    prefetchedWorkbenchRef.current = true;
    const conversations = useConversationStore.getState().getProjectConversations(projectId);
    const recentId = conversations[0]?.id;
    if (recentId) {
      router.prefetch(`/dashboard/projects/${projectId}/conversations/${recentId}`);
    }
  };
  const handleFilesSelected = (list: FileList | null) => {
    if (!list?.length) return;
    const allowed: File[] = [];
    const rejected: string[] = [];
    for (const file of Array.from(list)) {
      if (isAllowedChatFile(file)) allowed.push(file);else rejected.push(file.name);
    }
    if (rejected.length > 0) {
      toast.error(rejected.length === 1 ? `${rejected[0]} isn't supported. Use PDF or text files (.pdf, .txt, .md).` : `${rejected.length} files aren't supported (e.g. Word). Use PDF or text files only.`);
    }
    if (allowed.length > 0) {
      setFiles(prev => [...prev, ...allowed]);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const removeFile = (index: number) => {
    setFiles(prev_0 => prev_0.filter((_, i) => i !== index));
  };
  const removeContextMention = (index_0: number) => {
    setContextMentions(prev_1 => prev_1.filter((__0, i_0) => i_0 !== index_0));
  };
  const navigateToConversation = (conversationId: string) => {
    beginNavigating({
      conversationId
    });
    router.push(`/dashboard/projects/${projectId}/conversations/${conversationId}`);
  };
  const handleSubmit = async () => {
    const trimmed = text.trim();
    const hasContext = contextMentions.length > 0;
    const hasFiles = files.length > 0;
    if (!trimmed && !hasFiles && !hasContext || busy) return;
    const filesSnapshot = [...files];
    const contextSnapshot = [...contextMentions];
    const mode = resolveLaunchHandoffMode({
      fileCount: filesSnapshot.length,
      contextCount: contextSnapshot.length
    });
    const fileNames = filesSnapshot.map(f => f.name);
    const contextLabels = contextSnapshot.map(mention => mention.label);
    beginPreparing({
      fromRect: rectFromElement(fieldRef.current),
      mode,
      previewText: trimmed,
      fileNames,
      contextLabels
    });
    setCreating(true);
    try {
      const titleSeed = trimmed || filesSnapshot[0]?.name || contextSnapshot[0]?.label || "New conversation";
      const conversation = await createConversation(titleSeed.slice(0, MAX_TITLE_LENGTH), projectId);
      const promptText = trimmed || defaultLaunchMessageWhenEmpty(hasFiles, hasContext);
      setPrompt(conversation.id, {
        text: promptText,
        documents: [],
        contextMentions: contextSnapshot,
        documentsPending: hasFiles
      });
      setText("");
      setFiles([]);
      setContextMentions([]);
      navigateToConversation(conversation.id);
      if (hasFiles) {
        void completeLaunchAfterNavigate({
          projectId,
          conversationId: conversation.id,
          files: filesSnapshot
        });
      }
    } catch (e) {
      abortHandoff();
      toast.error(getErrorMessage(e, "Couldn't start the conversation."));
    } finally {
      setCreating(false);
    }
  };
  if (isViewer) return null;
  const canSubmit = (text.trim().length > 0 || files.length > 0 || contextMentions.length > 0) && !busy;
  const hasAttachments = files.length > 0 || contextMentions.length > 0;
  const attachmentHint = hasAttachments ? "Files and sources are included in your first message. Use @ in the workbench for later turns." : null;
  return <div className={cn("flex flex-col items-center gap-4 pt-2", className)}>
      <div className="flex flex-col items-center gap-2 text-center">
        <h2 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
          {greeting}
        </h2>
        {livenessLine ? waitingCount > 0 ? <Link href={buildProjectHomeWaitingDeepLink(projectId)} className="text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline">
              {livenessLine}
            </Link> : <p className="text-sm text-muted-foreground">{livenessLine}</p> : null}
        {/* Pulse = all-time totals; liveness = live ops (tab-local SSE + next-steps). */}
        <ProjectAnalyticsPulse projectId={projectId} className="text-muted-foreground/80" />
      </div>
      <form ref={fieldRef} id={PROJECT_HOME_INTENT_FIELD_ID} onSubmit={e_0 => {
      e_0.preventDefault();
      void handleSubmit();
    }} className={cn("relative flex w-full flex-col gap-1.5 rounded-2xl border-2 border-border bg-background px-3 py-2", "shadow-sm", "focus-within:border-ring/60 focus-within:ring-2 focus-within:ring-ring/20", hideFieldDuringHandoff && "opacity-0")} aria-hidden={hideFieldDuringHandoff || undefined}>
        {files.length > 0 || contextMentions.length > 0 ? <ul className="flex max-h-20 flex-wrap gap-1.5 overflow-y-auto px-0.5 pt-0.5" aria-label="Launch attachments and context">
            {contextMentions.map((mention_0, index_1) => <li key={`${mention_0.type}:${mention_0.id}`}>
                <LaunchContextChip mention={mention_0} onRemove={() => removeContextMention(index_1)} disabled={busy} />
              </li>)}
            {files.map((file_0, index_2) => <li key={`${file_0.name}-${file_0.size}-${index_2}`} className="inline-flex max-w-full items-center gap-1 rounded-md bg-muted/70 px-2 py-0.5 text-xs text-foreground">
                <span className="truncate">{file_0.name}</span>
                <button type="button" onClick={() => removeFile(index_2)} disabled={busy} className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-40" aria-label={`Remove ${file_0.name}`}>
                  <X className="size-3" aria-hidden />
                </button>
              </li>)}
          </ul> : null}
        <textarea ref={textareaRef} value={text} onChange={e_1 => setText(e_1.target.value)} onPaste={handleTextareaPaste} onFocus={prefetchWorkbenchShell} onKeyDown={e_2 => {
        if (e_2.key === "Enter" && !e_2.shiftKey) {
          e_2.preventDefault();
          void handleSubmit();
        }
      }} placeholder="Ask about this project, or start a conversation…" aria-label="Start a conversation" disabled={busy} rows={1} className={cn("min-h-[5.5rem] w-full resize-none bg-transparent", "px-0.5 pt-1.5 pb-1 text-base leading-normal outline-none", "placeholder:text-muted-foreground disabled:opacity-60")} />
        <input ref={fileInputRef} type="file" multiple accept={CHAT_ACCEPT_EXTENSIONS} className="hidden" onChange={e_3 => handleFilesSelected(e_3.target.files)} />
        <div className="flex items-center justify-end gap-1.5 pt-0.5">
          <ProjectHomeLaunchContextPicker projectId={projectId} open={contextPanelOpen} onOpenChange={setContextPanelOpen} selected={contextMentions} onSelectedChange={setContextMentions}>
            <button type="button" disabled={busy} onClick={isMdUp ? undefined : () => setContextPanelOpen(open => !open)} aria-label="Add context from project" aria-pressed={contextPanelOpen} aria-expanded={contextPanelOpen} className={cn(LAUNCH_COMPOSER_ACTION_CLASS, contextPanelOpen ? "bg-muted text-foreground" : "text-muted-foreground transition-colors hover:bg-muted hover:text-foreground")}>
              <Layers className="size-3.5" aria-hidden />
            </button>
          </ProjectHomeLaunchContextPicker>
          <button type="button" disabled={busy} onClick={() => fileInputRef.current?.click()} aria-label="Attach documents" className={cn(LAUNCH_COMPOSER_ACTION_CLASS, "text-muted-foreground transition-colors hover:bg-muted hover:text-foreground")}>
            <Paperclip className="size-3.5" aria-hidden />
          </button>
          <button type="submit" disabled={!canSubmit} aria-label="Start conversation" className={cn(LAUNCH_COMPOSER_ACTION_CLASS, "bg-primary text-primary-foreground transition-opacity hover:opacity-90")}>
            <ArrowUp className="size-3.5" aria-hidden />
            <span className="sr-only">Start conversation</span>
          </button>
        </div>
        {attachmentHint ? <p className="px-0.5 text-xs text-muted-foreground">{attachmentHint}</p> : null}
      </form>
    </div>;
}